import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";

import withRouter from "../../components/Common/withRouter";

// API
import { post } from "../../api/axios";
import endpoints from "../../api/endpoints";

// Utils
import { extractErrorMessage } from "../../utils/errorHandler";

// Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";

// reusable components
import CustomInput from "../../components/AuthenticationComponents/CustomInput";
import CustomButton from "../../components/AuthenticationComponents/CustomButton";
import logo from "../../assets/images/authentication/logo-auth.svg";
import "../../assets/scss/custom/pages/Authentication.scss";
import zapIcon from "../../assets/images/authentication/zap.svg";
import creditCardIcon from "../../assets/images/authentication/credit-card.svg";
import giftIcon from "../../assets/images/authentication/gift.svg";
import arrowRightIcon from "../../assets/images/authentication/arrow-right.svg";

const ForgetPassword = (props) => {
  document.title = "Forgot Password | Omaha Food Lovers";
  const location = useLocation();

  // Determine which portal based on route
  const isRestaurant = location.pathname === "/restaurant/forgot-password";
  const isAdmin = location.pathname === "/admin/forgot-password";

  // Configure toastr options
  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      email: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .trim()
        .email("Please enter a valid email")
        .required("Please enter your email")
        .max(255, "Email cannot exceed 255 characters")
        .matches(
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          "Please enter a valid email format",
        ),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError("");
      setSuccess("");
      try {
        // Use appropriate endpoint based on portal
        let endpoint;
        if (isAdmin) {
          endpoint = endpoints.adminForgotPassword;
        } else if (isRestaurant) {
          endpoint = endpoints.restaurantForgotPassword;
        } else {
          endpoint = endpoints.forgotPassword;
        }

        const response = await post(endpoint, {
          email: values.email,
        });

        if (response.data.success || response.data.status) {
          setSuccess(response.data.message || "Reset link sent to your email!");
          toastr.success(
            response.data.message || "Reset link sent to your email!",
          );
          validation.resetForm();
          // User will click the link in their email to reset password
        } else {
          setError(response.data.message || "Failed to send reset link");
          toastr.error(response.data.message || "Failed to send reset link");
        }
      } catch (error) {
        const errorMessage = extractErrorMessage(error);
        setError(errorMessage);
        toastr.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    document.body.className = "authentication-bg";
    return function cleanup() {
      document.body.className = "";
    };
  }, []);

  return (
    <React.Fragment>
      <div className="auth-page">
        <div className="auth-page-grid">
          {/* left brand / marketing panel */}
          <div className="auth-brand-panel">
            <div className="auth-brand-panel-inner">
              <Link to="/" className="auth-logo">
                <img
                  className="auth-logo-icon"
                  src={logo}
                  alt="Omaha Food Lovers"
                />
              </Link>

              <h1 className="auth-headline">
                {isAdmin ? (
                  <>
                    Admin Portal
                    <br />
                    <span className="auth-headline-accent">
                      Omaha Food Lovers
                    </span>
                  </>
                ) : isRestaurant ? (
                  <>
                    Restaurant Portal
                    <br />
                    <span className="auth-headline-accent">
                      Omaha Food Lovers
                    </span>
                  </>
                ) : (
                  <>
                    Earn rewards every time
                    <br />
                    you dine in{" "}
                    <span className="auth-headline-accent">Omaha</span>
                  </>
                )}
              </h1>

              <p className="auth-subline">
                {isAdmin ? (
                  <>
                    Manage the platform.
                    <br />
                    Oversee members, restaurants, and platform analytics.
                  </>
                ) : isRestaurant ? (
                  <>
                    Manage your restaurant account.
                    <br />
                    Track redemptions, view platform fees, and more.
                  </>
                ) : (
                  <>
                    Get 5% back at 200+ restaurants.
                    <br />
                    Track your dining streak. Unlock exclusive perks.
                  </>
                )}
              </p>

              <ul className="auth-feature-list">
                {isAdmin ? (
                  <>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={creditCardIcon} alt="Credit Card" />
                      </span>
                      <span>
                        Manage member accounts and restaurant listings.
                      </span>
                    </li>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={giftIcon} alt="Gift" />
                      </span>
                      <span>
                        View platform analytics and settlement reports.
                      </span>
                    </li>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={zapIcon} alt="Zap" />
                      </span>
                      <span>
                        Configure platform settings and manage permissions.
                      </span>
                    </li>
                  </>
                ) : isRestaurant ? (
                  <>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={creditCardIcon} alt="Credit Card" />
                      </span>
                      <span>
                        Track voucher redemptions and customer activity.
                      </span>
                    </li>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={giftIcon} alt="Gift" />
                      </span>
                      <span>Manage your restaurant profile and settings.</span>
                    </li>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={zapIcon} alt="Zap" />
                      </span>
                      <span>
                        Access platform analytics and settlement reports.
                      </span>
                    </li>
                  </>
                ) : (
                  <>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={creditCardIcon} alt="Credit Card" />
                      </span>
                      <span>
                        Link your card once. Earn 5% back automatically at 200+
                        local restaurants.
                      </span>
                    </li>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={giftIcon} alt="Gift" />
                      </span>
                      <span>
                        Redeem points for restaurant vouchers. Your rewards stay
                        local, supporting Omaha's dining scene.
                      </span>
                    </li>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={zapIcon} alt="Zap" />
                      </span>
                      <span>
                        100% free. No subscription, no hidden fees. Just eat and
                        earn.
                      </span>
                    </li>
                  </>
                )}
              </ul>
            </div>
          </div>

          {/* right form panel */}
          <div className="auth-form-panel">
            <div className="auth-form-card">
              <div className="auth-form-header">
                <h2>Forgot your password?</h2>
                <p>Enter your email and we'll send you a reset link.</p>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  validation.handleSubmit();
                  return false;
                }}
              >
                <CustomInput
                  label="Email"
                  name="email"
                  type="email"
                  placeholder="alex@omaha.com"
                  value={validation.values.email}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={
                    validation.touched.email && !!validation.errors.email
                  }
                  errorText={validation.errors.email}
                  autoComplete="email"
                  className="glass"
                />

                {error ? <p className="auth-server-error">{error}</p> : null}

                {success ? (
                  <p className="auth-server-success">{success}</p>
                ) : null}

                <div className="auth-submit-wrap">
                  <CustomButton
                    type="submit"
                    variant="primary"
                    iconPosition="right"
                    icon={arrowRightIcon}
                    loading={loading}
                  >
                    Send Reset Link
                  </CustomButton>
                </div>

                <p className="auth-login-link">
                  Back to <Link to="/login">Log In</Link>
                </p>
              </form>
            </div>
          </div>
        </div>
        <p className="auth-brand-footer">
          © Omaha Food Lovers {new Date().getFullYear()} - All Rights Reserved.
        </p>
      </div>
    </React.Fragment>
  );
};

export default withRouter(ForgetPassword);
